/* eslint-disable array-callback-return */
/* eslint-disable consistent-return */
import { format, formatDistanceToNow } from 'date-fns';
import { Customers, Users } from '@prisma/client';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { LoadingButton } from '@mui/lab';
import { Stack } from '@mui/material';
import GuestProfileRow from '../../components/customers/GuestProfileRow';
import { Customer, Section, SectionRow, User } from '../../interfaces';
import type { CustomerGuest } from '../../types/customers';
import {
  formatAddress,
  formatDollarAmount,
  formatPhone,
  getCleanedPhone,
} from '../global/helper';
import { CustomerOverview } from '../../types/customers';
import CustomerAppointmentRow from '../../components/customers/CustomerAppointmentRow';
import CustomerGiftCardRow from '../../components/customers/CustomerGiftCardRow';
import {
  getCustomerDetails,
  isAppointmentCompleted,
  isAppointmentInPast,
  isAppointmentPending,
  isAppointmentUpcoming,
} from '../../helpers/appointments';
import CustomerGeneralRequestRow from '../../components/customers/CustomerGeneralRequestRow';
import Badge from '../../components/global/Badge';
import { getFullName, trimString } from '../../helpers/global';
import { getCustomerBlockBadges } from '../../helpers/customers/customerBlockBadge';
import { PaymentsGroup } from '../../types/payments';

const constructCustomerName = (
  customer:
    | Customer
    | (Customers & {
        user: Users;
      }),
) => `${customer.user.firstName || ''} ${customer.user.lastName || ''}`.trim();

export const getPrimaryCustomerDetails = (
  primaryCustomer: ReturnType<typeof getCustomerDetails>['primaryCustomer'],
): SectionRow[] => {
  if (!primaryCustomer) return [];

  return [
    {
      type: 'TEXT',
      label: primaryCustomer.label,
      value: [
        getFullName(primaryCustomer),
        primaryCustomer.phoneNumber
          ? formatPhone(primaryCustomer.phoneNumber)
          : '',
        primaryCustomer.email,
        primaryCustomer.dob
          ? format(new Date(`${primaryCustomer.dob}T00:00:00`), 'MM/dd/yyyy')
          : '',
      ].filter((v): v is string => Boolean(v)),
    },
  ];
};

export const getCustomerRelationDetailRows = (
  customer: CustomerOverview | User | PaymentsGroup['customer'],
) => {
  if (!customer) return [];

  const rows: SectionRow[] = [];

  if (
    customer.streetAddress &&
    customer.city &&
    customer.state &&
    customer.zip
  ) {
    rows.push({
      type: 'TEXT',
      label: 'Address',
      value: [
        formatAddress(
          [
            customer.streetAddress,
            customer.streetAddress2,
            `${customer.city}, ${customer.state} ${customer.zip}`.trim(),
          ],
          true,
        ),
      ],
    });
  }

  if (customer.emergencyContacts?.length) {
    const emergencyContact = customer.emergencyContacts[0];
    rows.push({
      type: 'TEXT',
      label: 'Emergency Contact',
      value: [
        formatAddress(
          [
            getFullName(emergencyContact),
            emergencyContact.phoneNumber
              ? formatPhone(emergencyContact.phoneNumber)
              : '',
            emergencyContact.email,
            emergencyContact.streetAddress,
            emergencyContact.streetAddress2,
            emergencyContact.city
              ? `${emergencyContact.city}, ${emergencyContact.state} ${emergencyContact.zip}`.trim()
              : '',
          ],
          true,
        ),
      ],
    });
  }

  return rows;
};

export const getCustomerDetailsSection = (
  customer: CustomerOverview,
  name: string = 'Customer Details',
) => {
  const customerName = constructCustomerName(customer);
  const customerPhone = getCleanedPhone(customer.user.phoneNumber || '');
  let customerDob = '';
  if (customer.dob) {
    customerDob = new Date(customer.dob).toISOString()?.split('T')[0];
  }
  const customerSection: Section = {
    name,
    rows: [
      {
        type: 'TEXT',
        label: 'Name',
        value: [customerName || ''],
      },
      {
        type: 'TEXT',
        label: 'Email',
        value: [customer.user.email || ''],
      },
      {
        type: 'PHONE',
        label: 'Phone Number',
        value: [customerPhone || ''],
      },
      {
        type: 'TEXT',
        label: 'Birthday',
        value: [
          customerDob
            ? format(new Date(`${customerDob}T00:00:00`), 'MM/dd/yyyy')
            : '',
        ],
      },
      {
        type: 'TEXT',
        label: 'IG Handle',
        value: [customer.user.igHandle || ''],
      },
      'internalNotes' in customer
        ? {
            type: 'TEXT',
            label: 'Internal Notes',
            value: [customer.internalNotes || ''],
          }
        : null,
      ...getCustomerRelationDetailRows(customer),
    ].filter((row): row is SectionRow => Boolean(row)),
  };

  return customerSection;
};

export const getCustomerOverviewSection = (
  customer: CustomerOverview,
): Section | null => {
  if (!customer.summary) return null;

  const {
    lastAppointmentVisit,
    nextAppointmentVisit,
    isActive,
    totalVisits,
    numberOfNoShows,
    numberOfCanceledVisits,
    lifetimeValue,
    unusedDepositsTotal,
  } = customer.summary;

  const noShowRate =
    totalVisits > 0 ? ((numberOfNoShows / totalVisits) * 100).toFixed(0) : '0';

  const cancellationRate =
    totalVisits > 0
      ? ((numberOfCanceledVisits / totalVisits) * 100).toFixed(0)
      : '0';

  const dateTimeFormat = "MMM d, yyyy 'at' h:mmaaa";

  const formatLastAppointmentDate = (date: Date | null) => {
    if (!date) return '-';
    const dateObj = new Date(date);
    const formattedDate = format(dateObj, dateTimeFormat);
    const timeAgo = formatDistanceToNow(dateObj, { addSuffix: true });
    return `${formattedDate} (${timeAgo})`;
  };

  const formatNextAppointmentDate = (date: Date | null) => {
    if (!date) return '-';
    const dateObj = new Date(date);
    return format(dateObj, dateTimeFormat);
  };

  const giftCardBalance =
    customer.giftCards?.reduce(
      (acc, giftCard) => acc + giftCard.currentBalance,
      0,
    ) ?? 0;

  const blockBadges = getCustomerBlockBadges({ customer, isActive });

  const overviewRows: (SectionRow | null)[] = [
    {
      type: 'NODE',
      label: 'Customer Status',
      value: blockBadges.map((badge, index) => (
        <Badge
          key={`status-${badge.text}`}
          text={badge.text}
          colorScheme={badge.colorScheme}
          sx={{
            width: 'fit-content',
            ...(index > 0 ? { ml: 0.5 } : {}),
          }}
        />
      )),
    },
    giftCardBalance > 0
      ? {
          type: 'TEXT',
          label: 'Gift Card Balance',
          value: [formatDollarAmount(giftCardBalance / 100)],
        }
      : null,
    unusedDepositsTotal > 0
      ? {
          type: 'TEXT',
          label: 'Unused Deposits',
          value: [formatDollarAmount(unusedDepositsTotal / 100)],
        }
      : null,
    {
      type: 'TEXT',
      label: 'Last Appointment Visit',
      value: [formatLastAppointmentDate(lastAppointmentVisit)],
    },
    {
      type: 'TEXT',
      label: 'Next Appointment Visit',
      value: [formatNextAppointmentDate(nextAppointmentVisit)],
    },
    {
      type: 'TEXT',
      label: 'Number of Appointment Visits',
      value: [totalVisits.toString()],
    },
    {
      type: 'TEXT',
      label: 'No-Shows',
      value: [`${numberOfNoShows} (${noShowRate}% no-show rate)`],
    },
    {
      type: 'TEXT',
      label: 'Canceled Visits',
      value: [
        `${numberOfCanceledVisits} (${cancellationRate}% cancellation rate)`,
      ],
    },
    {
      type: 'TEXT',
      label: 'Total Lifetime Value',
      value: [`${formatDollarAmount(lifetimeValue / 100)} total spend`],
    },
    (customer.campaignAttributions?.length ?? 0) > 0
      ? {
          type: 'NODE',
          label: 'Campaign Attributions',
          value: [
            <Stack key="campaigns" direction="row" gap={0.5} flexWrap="wrap">
              {customer.campaignAttributions!.map(campaign => (
                <Badge
                  key={campaign.id}
                  text={trimString(campaign.name, 12)}
                  colorScheme="primary"
                  sx={{
                    width: 'fit-content',
                  }}
                />
              ))}
            </Stack>,
          ],
        }
      : null,
  ];

  const overviewSection: Section = {
    name: 'Customer Overview',
    rows: overviewRows.filter((row): row is SectionRow => Boolean(row)),
  };

  return overviewSection;
};

// Set modal data based on the employee data
export function createModalData(
  customer: CustomerOverview,
  {
    deleteFile,
    fileIdBeingDeleted,
    onSelectAppointment,
    onEditGuest,
    onSelectGiftCard,
  }: {
    deleteFile?: (fileId: string) => void;
    fileIdBeingDeleted?: string;
    onSelectAppointment?: () => void;
    onEditGuest?: (guest: CustomerGuest) => void;
    onSelectGiftCard?: (giftCardId: string) => void;
  } = {},
) {
  const upcomingAppointments = [...(customer.appointments ?? [])]
    ?.filter(
      appointment =>
        isAppointmentUpcoming(appointment) &&
        !isAppointmentCompleted(appointment),
    )
    .sort(
      (a, b) =>
        new Date(a.startDateTime!).getTime() -
        new Date(b.startDateTime!).getTime(),
    );

  const upcomingAppointmentSection: Section | undefined =
    (upcomingAppointments?.length || 0) > 0
      ? {
          name: 'Upcoming Appointments',
          rows: upcomingAppointments.map(appointment => ({
            id: appointment.appointmentId,
            type: 'NODE',
            label: '',
            value: [
              <CustomerAppointmentRow
                key={appointment.appointmentId}
                appointment={appointment}
                onSelectAppointment={onSelectAppointment}
              />,
            ],
          })),
          noGap: true,
        }
      : undefined;

  const openRequests = [
    ...(customer.appointments || []),
    ...(customer.generalRequests || []),
  ]
    .filter(appt => 'requestId' in appt || isAppointmentPending(appt))
    .sort(
      (a, b) =>
        new Date(b.updatedAt || '').getTime() -
        new Date(a.updatedAt || '').getTime(),
    );

  const openRequestsSection: Section | undefined =
    (openRequests?.length || 0) > 0
      ? {
          name: 'Open Requests',
          rows: openRequests.map(appointment => {
            const appointmentId =
              'appointmentId' in appointment
                ? appointment.appointmentId
                : appointment.requestId;
            return {
              id: appointmentId,
              type: 'NODE',
              label: '',
              value: [
                'appointmentId' in appointment ? (
                  <CustomerAppointmentRow
                    key={appointmentId}
                    appointment={appointment}
                    onSelectAppointment={onSelectAppointment}
                  />
                ) : (
                  <CustomerGeneralRequestRow
                    key={appointmentId}
                    generalRequest={appointment}
                    onSelectAppointment={onSelectAppointment}
                  />
                ),
              ],
            };
          }),
          noGap: true,
        }
      : undefined;

  const pastAppointments = [...(customer.appointments || [])]
    .filter(
      appointment =>
        isAppointmentInPast(appointment) || isAppointmentCompleted(appointment),
    )
    .sort(
      (a, b) =>
        new Date(b.startDateTime!).getTime() -
        new Date(a.startDateTime!).getTime(),
    );

  const pastAppointmentsSection: Section | undefined =
    (pastAppointments?.length || 0) > 0
      ? {
          name: 'Past Appointments',
          rows: pastAppointments.map(appointment => ({
            id: appointment.appointmentId,
            type: 'NODE',
            label: '',
            value: [
              <CustomerAppointmentRow
                key={appointment.appointmentId}
                appointment={appointment}
                onSelectAppointment={onSelectAppointment}
              />,
            ],
          })),
          noGap: true,
        }
      : undefined;

  const customerSection = getCustomerDetailsSection(customer);
  const customerOverviewSection = getCustomerOverviewSection(customer);

  const giftCardsSection: Section | null =
    (customer.giftCards?.length ?? 0) > 0
      ? {
          name: 'Gift Card(s)',
          rows: customer.giftCards!.map(giftCard => ({
            id: giftCard.id,
            type: 'NODE',
            label: '',
            value: [
              <CustomerGiftCardRow
                key={giftCard.id}
                giftCard={giftCard}
                onSelect={onSelectGiftCard}
              />,
            ],
          })),
          noGap: true,
        }
      : null;

  const guestProfilesSection: Section | null =
    (customer.customerGuests?.length ?? 0) > 0
      ? {
          name: `Guest Profiles (${customer.customerGuests!.length})`,
          rows: customer.customerGuests!.map(guest => ({
            id: guest.id,
            type: 'NODE',
            label: '',
            value: [
              <GuestProfileRow
                key={guest.id}
                guest={guest}
                onEdit={onEditGuest}
              />,
            ],
          })),
          noGap: true,
        }
      : null;

  // DO NOT CHANGE NAME without changing SideBarSection.tsx
  const transactions: Section = {
    name: 'Customer Transactions',
    rows: [],
    values: {
      customer,
    },
  };

  const filesSection: Section | null = customer.files?.length
    ? {
        name: 'Customer Files',
        rows: [
          {
            type: 'FILE',
            label: 'Files',
            value: customer.files.map(file => {
              const isDeleting = fileIdBeingDeleted === file.id;
              return {
                id: file.id,
                url: file.url,
                name: file.name,
                type: file.type.includes('image/') ? 'image' : 'pdf',
                actionComponent: deleteFile ? (
                  <LoadingButton
                    variant="light"
                    onClick={e => {
                      e.stopPropagation();
                      deleteFile(file.id);
                    }}
                    loading={isDeleting}
                    disabled={isDeleting}
                    sx={{
                      boxShadow: theme => theme.shadows[2],
                      bgcolor: theme => theme.palette.lightGrey.main,
                      border: 'none',
                      minWidth: 0,
                      p: 0.5,
                      fontSize: 'display.fontSize',
                      borderRadius: '50%',
                    }}>
                    <DeleteIcon
                      color="primary"
                      sx={{
                        opacity: isDeleting ? 0 : 1,
                      }}
                    />
                  </LoadingButton>
                ) : undefined,
                showName: true,
              };
            }),
          },
        ],
      }
    : null;

  const sections = [
    customerSection,
    guestProfilesSection,
    customerOverviewSection,
    giftCardsSection,
    filesSection,
    upcomingAppointmentSection,
    openRequestsSection,
    pastAppointmentsSection,
    transactions,
  ].filter((section): section is Section => Boolean(section));

  const newModalData = {
    sections,
  };

  return newModalData;
}
